import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { computed, observable } from 'mobx';
import { WordStore, MeaningStore } from '@seedlang/stores';
import { isPresent } from '@seedlang/utils';
import InPlaceText from 'components/form/in_place_text';
import { Link } from 'react-router';
import Copy from 'components/copy';
import autobind from 'autobind-decorator';
import WordSourceEdit from 'pages/builder/sources/word_source_edit';
import RegisterSelect from 'pages/builder/words/register_select';
import SynonymsEditModal from 'pages/builder/words/synonyms_edit_modal';
import DeleteButton from 'components/button/delete_button';
import cx from 'classnames';

const Wrapper = styled.div`
  .meanings-table {
    width: 100%;
    margin-top: 10px;
  }
  .sentence-item {
    margin-bottom: 8px;
  }
  .sentence-text {
    font-weight: bold;
  }
  .source-text {
    font-size: 12px;
    color: #666;
    padding-left: 10px;
  }
  .sources-cell {
    cursor: pointer;
    &:hover {
      background-color: #f5f5f5;
    }
  }
  .edit-sources-link {
    color: #1DB5D7;
    text-decoration: underline;
    font-size: 12px;
    margin-top: 5px;
    display: inline-block;
  }
  .synonyms-cell {
    cursor: pointer;
    &:hover {
      background-color: #f5f5f5;
    }
  }
`;

@observer
class MeaningsEdit extends React.Component {
  @observable editingSynonymsForMeaning = null;

  @autobind afterChange() {
    if (this.props.afterChange) {
      this.props.afterChange();
    }
  }

  @computed get word() {
    return this.props.word || WordStore.showData;
  }

  @computed get meanings() {
    const meanings = this.word.meanings || [];
    return meanings.slice().sort((a, b) => b.score - a.score);
  }

  @computed get hasMeanings() {
    return isPresent(this.meanings) && this.meanings.length > 0;
  }

  @autobind openSynonymsEdit(meaning) {
    this.editingSynonymsForMeaning = meaning;
  }

  @autobind closeSynonymsEdit() {
    this.editingSynonymsForMeaning = null;
  }

  @autobind deleteMeaning(meaningId) {
    MeaningStore.destroy({ ids: { meaningId } }, this.afterChange);
  }

  render() {
    if (!this.hasMeanings) {
      return null;
    }

    return (
      <Wrapper>
        {
          isPresent(this.editingSynonymsForMeaning) &&
            <SynonymsEditModal
              word={this.word}
              meaning={this.editingSynonymsForMeaning}
              afterChange={this.afterChange}
              onClose={this.closeSynonymsEdit}
            />
        }
        <div className='row'>
          <div className='col-xs-12'>
            <fieldset>
              <legend>{`Meanings (${this.meanings.length})`}</legend>
              <table className='table-wrapper meanings-table'>
                <thead>
                  <tr>
                    <th></th>
                    <th width='70'>Score</th>
                    <th width='70'>Level</th>
                    <th width='100'>Register</th>
                    <th>Sources</th>
                    <th>Synonyms</th>
                    <th>Example Sentences</th>
                    <th width='50'></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    this.meanings.map(meaning => {
                      return (
                        <tr key={meaning.id}>
                          <td>
                            <span style={{display: 'inline-flex', alignItems: 'center'}}>
                              {meaning.position}
                              <Copy text={meaning.id} margin='0 0 0 10px' />
                            </span>
                          </td>
                          <td>
                            {meaning.level.abbreviation}
                          </td>
                          <td>
                            <InPlaceText
                              model='meanings'
                              field='score'
                              id={meaning.id}
                              defaultValue={meaning.score}
                              afterChange={this.afterChange}
                              type='number'
                              step='10'
                              max='100'
                            />
                          </td>
                          <td>
                            <RegisterSelect
                              meaning={meaning}
                              word={this.word}
                              value={meaning.register}
                              afterChange={this.afterChange}
                            />
                          </td>
                          <td>
                            <WordSourceEdit
                              word={this.word}
                              meaning={meaning}
                              onChange={this.afterChange}
                            />
                          </td>
                          <td className='synonyms-cell' onClick={() => this.openSynonymsEdit(meaning)}>
                            <div
                              className={cx('text', 'editable', {
                                'blank-source': this.props.wordAssociation?.blankSource,
                              })}
                              onClick={() => this.openSynonymsEdit(meaning)}
                            >
                              {isPresent(meaning.synonyms) ? meaning.synonyms.map(synonym => synonym.targetText).join(', ') : '[No Synonym]'}
                            </div>
                          </td>
                          <td>
                            {
                              meaning.sentences && meaning.sentences.map(sentence => {
                                return (
                                  <div key={sentence.id} className='sentence-item'>
                                    <div className='sentence-text'>
                                      <Link
                                        to={{ name: 'builder.sentences.edit', params: { sentenceId: sentence.id } }}
                                        className='on-click'
                                        style={{marginRight: '5px'}}
                                      >
                                        <i className='fa fa-chevron-right fa-block' />
                                      </Link>
                                      {sentence.targetText}
                                    </div>
                                    {
                                      sentence.sources && sentence.sources.map(source => {
                                        return (
                                          <div key={source.id} className='source-text'>
                                            {source.text}
                                          </div>
                                        );
                                      })
                                    }
                                  </div>
                                );
                              })
                            }
                          </td>
                          <td style={{textAlign: 'center'}}>
                            <DeleteButton
                              className='fa-block right'
                              message={`Delete meaning ${meaning.position}?`}
                              onConfirm={() => this.deleteMeaning(meaning.id)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </fieldset>
          </div>
        </div>
      </Wrapper>
    );
  }
}

export default MeaningsEdit;